import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Truck, ShieldCheck, AlertCircle } from "lucide-react";

import { deviceAPI } from "../../services/api";
import { useAppDispatch } from "../../hooks/useStore";
import { pinLogin } from "../../store/slices/authSlice";
import { PinKeypad } from "../../components/devices/PinKeypad";
import {
  clearPairing,
  getPairedTruck,
  isPaired,
  savePairing,
  updatePairedTruck,
  onPairingCleared,
  DEVICE_UNPAIRED,
} from "../../utils/deviceSession";
import type { PairedTruck } from "../../types/devices";
import { vehicleLabel, vehiclePlateHint } from "../../utils/vehicleLabel";

/**
 * The screen bolted into a truck's cab — everything it can be before a driver
 * is signed in.
 *
 * One route (`/truck`) rather than two, because from the tablet's point of view
 * this is one place with two states, and which state it is in is not the
 * driver's business to know. A device is paired once, by whoever installs it,
 * and then shows a keypad for the rest of its life; nobody climbing into the cab
 * should have to understand why it occasionally asks for something else.
 *
 *   unpaired   a code from the office, entered once, ever
 *   keypad     a PIN, entered at the start of every shift
 *   signed in  not drawn here — the driver is sent to the truck's container list
 *
 * The state is derived rather than routed to, so a device that is quietly
 * revoked mid-shift lands back on the pairing form by itself: the interceptor
 * clears the dead pairing and sends the tablet here, and here reads storage.
 */

/** Six digits, matching PIN_LENGTH on the server. */
const PIN_LENGTH = 6;

/**
 * How often an idle keypad re-checks that it is still a truck's screen.
 *
 * The office side of pairing is immediate — revoke a device or reissue its code
 * and the admin's list says so at once. The cab side was not, and there is no
 * socket to make it so: realtime here authenticates with a driver's JWT, and a
 * keypad with nobody signed in has none. The only thing it holds is the device
 * secret.
 *
 * So the screen asks. Half a minute is chosen against the actual sequence —
 * somebody generates a code at a desk and walks to the truck — and the cost is
 * one request per idle tablet per thirty seconds, answered by a single indexed
 * read. It stops while the driver is signing in, because a poll must never
 * race a PIN, and it stops entirely once a driver is on the truck: from that
 * point the socket exists and `session:revoked` arrives on it.
 *
 * Only when the tab is visible. A tablet asleep in a parked cab asks nothing
 * and catches up on its first frame awake, which is also when anybody could
 * possibly be reading it.
 */
const CONTEXT_POLL_MS = 30_000;

/** Where a signed-in driver belongs — the truck's work, not a dashboard. */
const DRIVER_HOME = "/driver/containers";

export default function TruckDevicePage() {
  const { t } = useTranslation("devices");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [paired, setPaired] = useState(isPaired);
  const [truck, setTruck] = useState<PairedTruck | null>(getPairedTruck);

  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(paired);

  /**
   * Something went wrong fetching this screen's details — shown, never blocking.
   *
   * The keypad does not depend on any of it. `/devices/context` supplies the
   * plate and who is currently signed in, which are worth having and are not
   * worth standing between a driver and their shift: signing in needs the device
   * secret and a PIN, both of which this screen already has.
   *
   * It briefly *was* a blocking screen, and that was wrong in the way that
   * matters most here — a driver at a gate, at the start of a shift, looking at
   * a page with no keys on it because a status call had failed. Whatever is
   * wrong, the next thing they need is the keypad.
   */
  const [notice, setNotice] = useState<"moduleOff" | "unreachable" | null>(
    null,
  );

  /**
   * Confirm with the server that the stored pairing is still good.
   *
   * The tablet's own storage only says what was true when it was written: a
   * device revoked from the office, or re-paired onto a new screen, still has a
   * token sitting in `localStorage` that will fail on the first real request. So
   * the keypad checks before offering itself, and a dead pairing turns into the
   * pairing form here rather than into a confusing PIN rejection later.
   */
  const refreshContext = useCallback(async ({ silent = false } = {}) => {
    if (!isPaired()) {
      setChecking(false);
      return;
    }

    /**
     * A background check draws nothing.
     *
     * `checking` renders a full-screen spinner, which is right for the first
     * load and quite wrong for the poll that keeps this screen honest — it
     * would blink the keypad away every thirty seconds in front of a driver
     * trying to type on it. The same goes for `notice`: a poll that fails on a
     * flaky yard connection must not replace a working keypad's header with a
     * warning. Both are left to the foreground path, and the one answer that
     * genuinely changes the screen — `DEVICE_UNPAIRED` — is acted on either way.
     */
    if (!silent) {
      setChecking(true);
      setNotice(null);
    }
    try {
      const res = await deviceAPI.context();
      const { device } = res.data.data;
      /**
       * Only when it actually moved.
       *
       * The poll above runs this every thirty seconds, and `setTruck` with a
       * fresh object identity re-renders the whole screen each time for a plate
       * that changes about never. The keypad is fully controlled from this
       * component, so nothing typed is lost either way — but a screen that
       * re-renders under somebody's thumb for no reason is a screen that will
       * eventually be blamed for a mis-keyed digit.
       */
      setTruck((current) =>
        current &&
        current._id === device.truck?._id &&
        current.registrationNumber === device.truck?.registrationNumber &&
        current.displayName === device.truck?.displayName &&
        current.markAndModel === device.truck?.markAndModel
          ? current
          : device.truck,
      );
      // The keypad had the truck from storage already; this is the server's
      // current version of it, so the cache every other screen reads catches up
      // with a plate the office may have edited since pairing day.
      updatePairedTruck(device.truck);
      setPaired(true);
    } catch (e: any) {
      /**
       * Only one answer justifies forgetting the pairing.
       *
       * This used to discard it on *any* failure, which was wrong in two ways
       * that both cost a trip to the office for a fresh code:
       *
       *   a 404, meaning the tenant has the vehicles module switched off — the
       *   pairing is fine and will work again the moment it is switched back on;
       *
       *   a network error, which on a tablet that boots in a yard before the
       *   wifi associates is the ordinary morning case. The screen unpaired
       *   itself over a few seconds of no signal.
       *
       * So the pairing is dropped only when the server has actually said it does
       * not recognise this device. Everything else keeps it and says what is
       * wrong.
       */
      if (e?.response?.data?.code === DEVICE_UNPAIRED) {
        clearPairing();
        setPaired(false);
        setTruck(null);
      } else if (!silent) {
        // 404 is the module guard: it answers exactly like a route that was
        // never mounted, on purpose, so there is no code to read — the status
        // is the whole signal.
        setNotice(e?.response?.status === 404 ? "moduleOff" : "unreachable");
      }
    } finally {
      if (!silent) setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshContext();
  }, [refreshContext]);

  /**
   * The pairing was dropped by something other than this screen.
   *
   * Which is the normal case, not an edge one: the axios interceptor forgets a
   * dead pairing from whatever request discovered it, and until this listener
   * existed the keypad went on drawing itself over storage that no longer had a
   * token in it. An admin reissuing a code produced exactly the report that
   * started this: the driver types their PIN, is told "this device is no longer
   * paired" *under the keypad*, and the pairing form only appears if somebody
   * thinks to reload.
   *
   * Flipping here means the tablet shows the pairing form the moment the server
   * says so — on the failed PIN, or on the next poll below, whichever reaches it
   * first.
   */
  useEffect(
    () =>
      onPairingCleared(() => {
        setPaired(false);
        setTruck(null);
        setPin("");
        setChecking(false);
      }),
    [],
  );

  /**
   * An idle keypad keeps asking whether it is still this truck's screen.
   *
   * This is what turns "the tablet finds out when somebody tries to use it"
   * into "the tablet finds out" — so a revoked or re-paired device shows the
   * pairing form by itself, within the poll window, with nobody standing in
   * front of it. `refreshContext` already does the right thing with every
   * answer: `DEVICE_UNPAIRED` forgets the pairing, a 404 says the module is
   * off, a network error changes nothing.
   *
   * Deliberately not running while `busy`, so a poll cannot land between a PIN
   * being submitted and its answer arriving, and not running at all while the
   * screen is unpaired — there is nothing to check and the admin is mid-typing.
   */
  useEffect(() => {
    if (!paired || busy) return;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      refreshContext({ silent: true });
    };

    const timer = window.setInterval(tick, CONTEXT_POLL_MS);
    // A tablet woken in the cab should not wait out the rest of an interval it
    // slept through.
    document.addEventListener("visibilitychange", tick);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [paired, busy, refreshContext]);

  const submitPairing = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || busy) return;

    setBusy(true);
    setError(null);
    try {
      const res = await deviceAPI.pair(code.trim());
      const { deviceToken, device } = res.data.data;

      savePairing({ deviceToken, truck: device.truck, label: device.label });
      setTruck(device.truck);
      setPaired(true);
      setCode("");
    } catch (e: any) {
      setError(e.response?.data?.message || t("pair.failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (entered: string) => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(pinLogin(entered)).unwrap();
      navigate(DRIVER_HOME, { replace: true });
    } catch (e: any) {
      /**
       * The entry is cleared on any failure.
       *
       * A rejected PIN left in the boxes invites the driver to "fix" one digit,
       * which is how somebody burns five attempts on a PIN that was never close.
       * Starting over is also what every keypad they have ever used does.
       */
      setPin("");

      /**
       * A disabled module is not a wrong PIN.
       *
       * The guard answers a bare 404, so left alone it would surface as "Not
       * found" under the dots — which a driver reads as "my PIN is wrong" and
       * responds to by typing it again, and again, into a door that is not
       * locked but bricked up.
       */
      // A 404 here is the module guard, not a wrong PIN. Said as the notice
      // above the keys rather than as an error under them, because "that PIN is
      // not recognised" would send the driver off to find a colleague.
      if (e?.status === 404 || e?.response?.status === 404) {
        setNotice("moduleOff");
        return;
      }

      setError(typeof e === "string" ? e : t("pin.failed"));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="driver-ui p-safe flex min-h-screen-safe items-center justify-center bg-[#FAFAF8]">
        <Loader2 className="h-6 w-6 animate-spin text-[#8A9590]" />
      </div>
    );
  }

  // ── Not paired: the one-time setup, done by whoever installs the screen ────
  if (!paired) {
    return (
      <div className="driver-ui p-safe flex min-h-screen-safe items-center justify-center bg-[#FAFAF8] px-4">
        <form
          onSubmit={submitPairing}
          className="w-full max-w-sm rounded-3xl border border-[#ECEAE4] bg-white p-6 shadow-sm"
        >
          <div className="mb-5 flex flex-col items-center text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F0EEE8]">
              <ShieldCheck className="h-6 w-6 text-[#C97A3C]" />
            </span>
            <h1 className="text-lg font-semibold text-[#0B0F0E]">
              {t("pair.title")}
            </h1>
            <p className="mt-1 text-sm text-[#8A9590]">{t("pair.subtitle")}</p>
          </div>

          <label
            htmlFor="pairing-code"
            className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-[#8A9590]"
          >
            {t("pair.codeLabel")}
          </label>
          <input
            id="pairing-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            // The code is alphanumeric and read off a screen, so the OS should
            // not autocorrect it, capitalise it again, or offer to remember it.
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            placeholder="XXXX-XXXX"
            className="w-full rounded-xl border border-[#D4D2CA] px-4 py-3 text-center font-mono text-xl tracking-widest text-[#0B0F0E] outline-none focus:border-[#C97A3C]"
          />

          {error && (
            <p className="mt-3 flex items-start gap-1.5 text-sm text-[#E24B4A]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !code.trim()}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B0F0E] py-3 font-medium text-white transition-opacity disabled:opacity-40"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("pair.submit")}
          </button>

          <p className="mt-4 text-center text-xs text-[#A6A49C]">
            {t("pair.help")}
          </p>
        </form>
      </div>
    );
  }

  // ── Paired: the keypad every shift starts at ──────────────────────────────
  // Full-bleed and vertically centred, so this is the screen a notch and a home
  // indicator crop. `min-h-screen-safe` also shortens it under the keyboard, so
  // the code field cannot push the keypad off the bottom.
  return (
    <div className="driver-ui p-safe flex min-h-screen-safe flex-col items-center justify-center bg-[#FAFAF8] px-4 py-8">
      <div className="mb-8 flex flex-col items-center text-center">
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F0EEE8]">
          <Truck className="h-6 w-6 text-[#2F7A5B]" />
        </span>

        {/* The truck's name first and largest: before anything else, the driver
            has to be sure this is the screen for the truck they are standing in.
            The plate follows when the name is the fleet's own. */}
        <h1 className="text-2xl font-semibold tracking-wide text-[#0B0F0E]">
          {(truck && vehicleLabel(truck)) || t("keypad.unknownTruck")}
        </h1>
        {(vehiclePlateHint(truck) || truck?.markAndModel) && (
          <p className="text-sm text-[#8A9590]">
            {[vehiclePlateHint(truck), truck?.markAndModel].filter(Boolean).join(" · ")}
          </p>
        )}

        <p className="mt-4 text-sm text-[#5A6460]">{t("keypad.prompt")}</p>

        {/*
          Whatever went wrong, said once and out of the way.

          Above the keys rather than in place of them: a driver who can still
          sign in should, and one who cannot needs to know why rather than to
          find a blank screen. `Retry` re-reads the details; it is not a
          precondition for typing a PIN.
        */}
        {notice && (
          <div className="mt-3 max-w-xs rounded-xl bg-[#FFF4E5] px-3 py-2 text-xs text-[#8A5A1E]">
            <p>{t(`notice.${notice}`)}</p>
            <button
              type="button"
              onClick={refreshContext}
              disabled={checking}
              className="mt-1 font-medium underline underline-offset-2 disabled:opacity-50"
            >
              {t("notice.retry")}
            </button>
          </div>
        )}
      </div>

      <PinKeypad
        value={pin}
        length={PIN_LENGTH}
        disabled={busy}
        onChange={setPin}
        onComplete={submitPin}
        labels={{ clear: t("keypad.clear"), delete: t("keypad.delete") }}
      />

      <div className="mt-6 h-10 text-center">
        {busy ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#8A9590]" />
        ) : error ? (
          <p className="flex items-center justify-center gap-1.5 text-sm text-[#E24B4A]">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </p>
        ) : null}
      </div>

      <p className="mt-4 text-center text-xs text-[#A6A49C]">
        {t("keypad.help")}
      </p>
    </div>
  );
}
